// Add CSS variables to :root when the app initializes and watch for changes
import useAuthStore from '../stores/auth';
import { watch } from 'vue';

export default defineNuxtPlugin(() => {
   if (!process.client) {
      return;
   }

   const config = useRuntimeConfig().public;
   const authStore = useAuthStore();

   // Only write a CSS variable when we actually have a value to override with.
   // Base brand colors live in CSS (:root) as the single source of truth, so an
   // absent value means "leave the CSS-defined color in place" rather than
   // clobbering it with a hardcoded fallback.
   const setRootColor = (name: string, value?: string | null) => {
      if (!value) {
         return;
      }
      document.documentElement.style.setProperty(name, value);
   };

   // Override the CSS-defined base colors only for runtime cases that CSS can't
   // express: the USPA portal swap and API-driven per-user color schemes. IPA
   // provides no brand-color env vars, so its :root colors are left untouched.
   const updateCssVariables = () => {
      const isUspaPortal = authStore?.user?.site_settings?.portal === 'uspa';

      const primaryColor = isUspaPortal ? config.uspaPrimaryColor : config.primaryColor;
      const secondaryColor = isUspaPortal ? config.uspaSecondaryColor : config.secondaryColor;

      setRootColor('--lms-color-brand-primary', primaryColor as string);
      setRootColor('--lms-color-brand-secondary', secondaryColor as string);

      // API-driven color scheme overrides (authenticated pages only)
      const colorScheme = authStore?.user?.site_settings?.color_scheme;
      setRootColor('--lms-color-button-bg', colorScheme?.button_background_color);
      setRootColor('--lms-color-brand-title', colorScheme?.main_title_color);
      setRootColor('--lms-color-footer-bg', colorScheme?.footer_background_color);
   };

   // Initial update
   updateCssVariables();

   // Watch for changes in auth store
   watch(() => [
      authStore?.user?.site_settings?.portal,
      authStore?.user?.site_settings?.color_scheme,
   ], () => {
      updateCssVariables();
   }, { immediate: false, deep: true });
});
